/**
 * 회사 브랜드(로고·회사명) 공유 스토어 — 관리 콘솔 "회사정보 관리"가 SSOT (2026-08-12).
 *
 * 관리자가 8444 에서 로고를 등록하면 사용자 사이트 좌측 레일 브랜드가 그 로고로 교체된다.
 * 구조는 menu-config.ts 싱글턴과 같은 이유로 동일하다: 소비자가 늘어도 요청은 **앱당 1회**,
 * 결과는 구독자 전원에게 같은 스냅샷으로 나간다(per-IP rate limit 압박 회피 —
 * [[ratelimit-429-served-as-http-200]]).
 *
 * 폴백 순서:
 *   1) localStorage 캐시(같은 institution 일 때만) — 첫 페인트 플래시 봉합
 *   2) GET /institution 응답 — 도착하면 캐시 갱신 + 전 구독자 리렌더
 *   3) 조회 실패 → logo=null (NavBar 가 기본 심볼 'A' + 워드마크로 폴백)
 *
 * 갱신 시점: 앱당 1회 + 탭이 다시 보일 때 TTL(60초) 경과 시 재조회. 관리자가 로고를 바꾸면
 * 사용자는 강력 새로고침 없이 탭 전환만으로 반영된다.
 */

import { useSyncExternalStore } from 'react';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';
const CACHE_KEY = 'wc.brand.cache.v1';
const REVALIDATE_MS = 60_000;
const WC_LOGO_RE = /^wclogo:(\d+)$/;

export interface BrandState {
  /** 표시용 로고 URL. null = 미등록(기본 심볼 폴백). */
  logoSrc: string | null;
  /** 회사명 — 로고 alt 텍스트. null = 미조회. */
  companyName: string | null;
}

interface CachedBrand {
  instId: string;
  logoUrl: string | null;
  name: string | null;
}

/** raw logoUrl(`wclogo:{ts}` | http(s) URL) → <img src>. 그 외/빈 값은 null. */
export function brandLogoSrc(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const m = raw.match(WC_LOGO_RE);
  // ?v={ts} = 캐시 무효화 토큰(로고 교체 시 값이 바뀐다). 쿠키 인증으로 <img> 가 직접 로드.
  if (m) return `${API_BASE}/institution/logo?v=${m[1]}`;
  return null;
}

function currentInstId(): string | null {
  const id = useAuthStore.getState().user?.institutionId;
  return id === undefined || id === null ? null : String(id);
}

/**
 * 캐시 읽기 — **같은 institution 일 때만** 사용한다. 계정이 바뀌었는데 이전 회사 로고를
 * 잠깐이라도 보여주는 일이 없도록(공유 PC) instId 를 함께 저장해 대조한다.
 */
function readCache(): CachedBrand | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as CachedBrand;
    if (!c || typeof c.instId !== 'string') return null;
    const inst = currentInstId();
    if (inst !== null && c.instId !== inst) return null;
    return c;
  } catch {
    return null;
  }
}

function stateFrom(c: CachedBrand | null): BrandState {
  return { logoSrc: brandLogoSrc(c?.logoUrl ?? null), companyName: c?.name ?? null };
}

let state: BrandState = stateFrom(readCache());
let lastFetchedAt = 0;
let inflight = false;
let started = false;

const subscribers = new Set<() => void>();

function setState(next: BrandState): void {
  if (next.logoSrc === state.logoSrc && next.companyName === state.companyName) return;
  state = next;
  for (const fn of subscribers) fn();
}

/**
 * GET /institution 조회 — TTL 내 재호출은 무시(force 로 강제).
 *
 * ⚠ try/catch 로 감싼 이유는 menu-config.ts 와 같다: subscribe 안에서 불리므로 동기 throw
 *   (jsdom 등)가 밖으로 새면 구독 자체가 터져 화면이 렌더되지 않는다.
 */
export function loadBrand(force = false): void {
  if (inflight) return;
  if (!force && lastFetchedAt !== 0 && Date.now() - lastFetchedAt < REVALIDATE_MS) return;
  inflight = true;
  try {
    apiClient
      .get<{ institution: { logoUrl?: string | null; name?: string | null } }>('/institution')
      .then((res) => {
        const inst = res.data?.institution;
        const logoUrl = inst?.logoUrl ?? null;
        const name = inst?.name ?? null;
        const instId = currentInstId();
        if (instId !== null) {
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ instId, logoUrl, name })); } catch { /* no-op */ }
        }
        lastFetchedAt = Date.now();
        setState({ logoSrc: brandLogoSrc(logoUrl), companyName: name });
      })
      // 조회 실패(401/네트워크 등) — 캐시가 있으면 유지, 없으면 기본 심볼 폴백. 재시도는 다음 TTL 에.
      .catch(() => { lastFetchedAt = Date.now(); })
      .finally(() => { inflight = false; });
  } catch {
    inflight = false;
  }
}

function ensureStarted(): void {
  if (started) return;
  started = true;
  loadBrand(true);
  // 탭이 다시 보일 때 TTL 경과분만 재조회 — 관리 콘솔 변경을 새로고침 없이 따라잡는다.
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') loadBrand();
    });
  } catch { /* no-op (jsdom/SSR) */ }
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  ensureStarted();
  return () => { subscribers.delete(fn); };
}

function getSnapshot(): BrandState {
  return state;
}

/** 브랜드 스냅샷 구독 — NavBar 등 소비자 공용. */
export function useBrand(): BrandState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** 테스트 전용 — 모듈 싱글턴 초기화. */
export function _resetBrandForTests(): void {
  state = { logoSrc: null, companyName: null };
  lastFetchedAt = 0;
  inflight = false;
  started = false;
  subscribers.clear();
  try { localStorage.removeItem(CACHE_KEY); } catch { /* no-op */ }
}
